/**
 * Proactive refresh — schedule platform refresh before access expiry (UX timer)
 * or config interval when HttpOnly access has no readable exp.
 */
import { useEffect, useRef } from 'react';
import { parseAccessTokenExpMs } from './jwtUx.js';
import { attemptSessionRefresh, isHardSessionRefreshRevocation } from './sessionRefresh.js';
import { endCustomerSessionGlobal } from './customerAuthSync.js';
import { useRuntimeConfig } from '../providers/RuntimeConfigProvider.js';
import { useCustomerSession } from './CustomerSessionProvider.js';

const EXPIRY_SKEW_MS = 7_000;
const MIN_DELAY_MS = 5_000;
/** Cookie mode has no readable JWT exp — refresh before access Max-Age (~15m). */
export const COOKIE_MODE_REFRESH_LEAD_MS = 60_000;

/**
 * Delay until the next proactive refresh. Cookie-mode (no exp) fires 1 minute
 * before `intervalCapMs` so periodic `/me` does not race a dead access cookie.
 */
export function resolveProactiveRefreshDelayMs(
  intervalCapMs: number,
  expMs: number | null,
  nowMs: number = Date.now(),
): number {
  if (expMs !== null) {
    const untilExp = Math.max(MIN_DELAY_MS, expMs - nowMs - EXPIRY_SKEW_MS);
    if (intervalCapMs > 0) {
      return Math.min(untilExp, intervalCapMs);
    }
    return untilExp;
  }
  if (intervalCapMs <= 0) {
    return intervalCapMs;
  }
  if (intervalCapMs > COOKIE_MODE_REFRESH_LEAD_MS + MIN_DELAY_MS) {
    return intervalCapMs - COOKIE_MODE_REFRESH_LEAD_MS;
  }
  return Math.max(MIN_DELAY_MS, Math.floor(intervalCapMs * 0.85));
}

export function useProactiveSessionRefresh(intervalCapMs: number): void {
  const { session } = useCustomerSession();
  const config = useRuntimeConfig();
  const timerRef = useRef<number | null>(null);
  const refreshInFlightRef = useRef(false);

  useEffect(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    // No proactive refresh without live session.
    if (session === null) {
      return undefined;
    }

    const clearTimer = (): void => {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const schedule = (): void => {
      clearTimer();
      if (session === null) {
        return;
      }
      const delay = resolveProactiveRefreshDelayMs(
        intervalCapMs,
        parseAccessTokenExpMs(null),
      );

      timerRef.current = window.setTimeout(() => {
        timerRef.current = null;
        if (document.visibilityState !== 'visible') {
          schedule();
          return;
        }
        if (refreshInFlightRef.current) {
          return;
        }
        refreshInFlightRef.current = true;
        void (async () => {
          try {
            const result = await attemptSessionRefresh(config);
            if (!result.ok && isHardSessionRefreshRevocation(result.code)) {
              await endCustomerSessionGlobal({
                broadcast: true,
                callApi: false,
                reason: 'expired',
              });
              return;
            }
            if (result.ok) {
              schedule();
            }
          } finally {
            refreshInFlightRef.current = false;
          }
        })();
      }, delay);
    };

    schedule();

    const onVisible = (): void => {
      if (document.visibilityState !== 'visible' || refreshInFlightRef.current) {
        return;
      }
      schedule();
    };
    document.addEventListener('visibilitychange', onVisible);

    return (): void => {
      clearTimer();
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [config, intervalCapMs, session]);
}
